import { useQuery } from "@apollo/client";
import moment from "moment";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  GetMeetingTranscriptQueryQuery,
  GetMeetingTranscriptQueryQueryVariables,
  MeetingBotSetting,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  RecallBotStatus,
} from "types/graphql-schema";

import getMeetingTranscriptQuery from "@apps/meeting-transcription-dialog/graphql/get-meeting-transcript-query";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import {
  getMeetingVideoTimeFromSearch,
  updateMeetingSearch,
} from "../meeting-page-url-state";
import MeetingSidebarAIContainer from "../meeting-sidebar/ai-container";
import MeetingSummaryGroupTranscription from "./summary-group-transcription";

// Keys persisted in localStorage when a user dismisses one of the
// actionable / informational Summary cards. Each entry is scoped to the
// meeting group (series) AND state type, so dismissing "AI Summary
// Unavailable" on one meeting suppresses the same card on every other
// meeting in the series showing the same state — switching to a
// different state on the same series still surfaces a card.
const DISMISSED_STORAGE_KEY = "meeting-summary-dismissed-states";

type DismissibleStateKey =
  | "summarization-disabled"
  | "in-meeting"
  | "scheduled"
  | "joining"
  | "no-recap-cta";

const scopedDismissalKey = (
  meetingGroupId: number,
  stateKey: DismissibleStateKey,
) => `group-${meetingGroupId}:${stateKey}`;

const readDismissedFromStorage = (): Set<string> => {
  try {
    const stored = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!stored) return new Set();
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
};

const writeDismissedToStorage = (next: Set<string>) => {
  try {
    window.localStorage.setItem(
      DISMISSED_STORAGE_KEY,
      JSON.stringify(Array.from(next)),
    );
  } catch {
    // Storage unavailable — fall back to in-memory state only.
  }
};

// Shared store so dismissing on one rendered meeting card immediately
// updates every other MeetingSummary instance on the page (e.g. multiple
// meetings in the same series stacked on the profile 1-on-1 tab). Without
// this, each hook instance keeps its own snapshot and only the dismissing
// card disappears until the next page load.
let inMemoryDismissed: Set<string> | null = null;
const dismissedListeners = new Set<() => void>();

const getDismissedSnapshot = (): Set<string> => {
  if (inMemoryDismissed === null) {
    inMemoryDismissed = readDismissedFromStorage();
  }
  return inMemoryDismissed;
};

const subscribeToDismissed = (cb: () => void) => {
  dismissedListeners.add(cb);
  return () => {
    dismissedListeners.delete(cb);
  };
};

const useDismissedStates = () => {
  const dismissed = useSyncExternalStore(
    subscribeToDismissed,
    getDismissedSnapshot,
    getDismissedSnapshot,
  );

  const dismiss = useCallback((scopedKey: string) => {
    const current = getDismissedSnapshot();
    if (current.has(scopedKey)) return;
    const next = new Set(current);
    next.add(scopedKey);
    inMemoryDismissed = next;
    writeDismissedToStorage(next);
    dismissedListeners.forEach((listener) => listener());
  }, []);

  return {
    isDismissed: (scopedKey: string) => dismissed.has(scopedKey),
    dismiss,
  };
};

const MeetingSummary = ({
  meeting,
  meetingGroupId,
  isCurrentMeeting = false,
  autoOpenVideoTranscriptModal = false,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  meetingGroupId: number;
  isCurrentMeeting?: boolean;
  autoOpenVideoTranscriptModal?: boolean;
}) => {
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [showVideoTranscriptModal, setShowVideoTranscriptModal] =
    useState(false);
  const { isDismissed, dismiss } = useDismissedStates();
  const history = useHistory();
  const location = useLocation();

  // Deep links like `?videoTime=7.668` need to open the Video/Transcript
  // modal so the <video> element mounts — otherwise the seek effect inside
  // summary-group-transcription has nothing to seek against.
  useEffect(() => {
    if (
      autoOpenVideoTranscriptModal &&
      getMeetingVideoTimeFromSearch(location.search) !== null
    ) {
      setShowVideoTranscriptModal(true);
    }
  }, [autoOpenVideoTranscriptModal, location.search]);

  const handleCloseVideoTranscriptModal = useCallback(() => {
    setShowVideoTranscriptModal(false);
    // Drop the deep-link param so the auto-open effect doesn't immediately
    // reopen the modal on the next render.
    if (getMeetingVideoTimeFromSearch(location.search) !== null) {
      history.replace({
        pathname: location.pathname,
        search: updateMeetingSearch({
          search: location.search,
          videoTimeInSeconds: null,
        }),
        hash: location.hash,
      });
    }
  }, [history, location.hash, location.pathname, location.search]);

  const summarizationNotEnabled =
    !meeting.organization?.enableMeetingSummarization;

  const { data, loading, refetch } = useQuery<
    GetMeetingTranscriptQueryQuery,
    GetMeetingTranscriptQueryQueryVariables
  >(getMeetingTranscriptQuery, {
    skip: summarizationNotEnabled,
    variables: { meetingId: meeting.id },
  });

  const transcriptMeeting = data?.meeting ?? null;
  const transcript = transcriptMeeting?.transcript;
  const hasTranscriptData =
    (transcript?.orderedTranscriptData?.length ?? 0) > 0;
  const hasVideoData = !!(
    transcript?.videoRecording ||
    transcript?.recallVideoRecording ||
    transcript?.canSaveVideoRecording
  );

  // Mirrors ai-container's branching to know which state is being rendered.
  // Used to: (a) pick the dismissal storage key for this card and (b) decide
  // whether to render the collapsible Summary wrapper.
  const meetingHasNotStartedYet =
    !!meeting.startDatetime && moment().isBefore(meeting.startDatetime);
  const meetingIsProbablyDone =
    !!meeting.endDatetime &&
    moment().isAfter(moment(meeting.endDatetime).add(1, "hours"));
  const notetakerWillJoin =
    meeting.meetingGroup?.meetingBotSetting === MeetingBotSetting.AlwaysJoin ||
    (meeting.meetingGroup?.meetingBotSetting ===
      MeetingBotSetting.UseParticipantsSetting &&
      meeting.botParticipationDetails.willBotJoin);

  const dismissibleStateKey: DismissibleStateKey | null =
    summarizationNotEnabled
      ? "summarization-disabled"
      : !transcriptMeeting
        ? null
        : transcriptMeeting.transcript || transcriptMeeting.notesSummary
          ? null
          : transcriptMeeting.botStatus === RecallBotStatus.GeneratingSummary
            ? // GeneratingSummary is transient — never dismissible. The card
              // shows a spinner instead of an X.
              null
            : transcriptMeeting.botStatus === RecallBotStatus.InMeeting
              ? "in-meeting"
              : transcriptMeeting.botStatus === RecallBotStatus.Scheduled
                ? "scheduled"
                : transcriptMeeting.botStatus === RecallBotStatus.JoiningMeeting
                  ? "joining"
                  : transcriptMeeting.isRecurring &&
                      transcriptMeeting.meetingGroup?.canUpdate?.permission &&
                      ((!meetingHasNotStartedYet && !meetingIsProbablyDone) ||
                        !notetakerWillJoin)
                    ? "no-recap-cta"
                    : null;

  // Hide the entire Summary section (including the collapsible header) when
  // the only branches ai-container would render are the bare "Meeting not
  // found" / "No meeting recap available" fallbacks. Keep it visible while
  // loading and for every actionable state.
  const isBareNoRecap =
    !!transcriptMeeting &&
    !transcriptMeeting.transcript &&
    !transcriptMeeting.notesSummary &&
    transcriptMeeting.botStatus !== RecallBotStatus.GeneratingSummary &&
    transcriptMeeting.botStatus !== RecallBotStatus.InMeeting &&
    transcriptMeeting.botStatus !== RecallBotStatus.Scheduled &&
    transcriptMeeting.botStatus !== RecallBotStatus.JoiningMeeting &&
    !(
      transcriptMeeting.isRecurring &&
      transcriptMeeting.meetingGroup?.canUpdate?.permission
    );
  const isMeetingNotFound =
    !summarizationNotEnabled && !loading && !transcriptMeeting;
  if (isMeetingNotFound || isBareNoRecap) return null;
  if (
    dismissibleStateKey &&
    isDismissed(scopedDismissalKey(meetingGroupId, dismissibleStateKey))
  )
    return null;

  // The Summary collapsible header only wraps the generated-summary success
  // state. The loading state and the actionable CTA / info states render
  // standalone (no wrapper) — loading and GeneratingSummary show a spinner
  // in the top-right; the rest show a dismiss X.
  const hasGeneratedSummary =
    !!transcriptMeeting &&
    !!(transcriptMeeting.transcript || transcriptMeeting.notesSummary);
  const showSummaryWrapper = hasGeneratedSummary;

  // If the meeting hasn't reached its scheduled end time, only render the
  // section when we already have a generated summary. This covers cases like
  // a team finishing the meeting early — the summary still surfaces — while
  // keeping upcoming meetings free of spurious "scheduled" / loading cards.
  const meetingHasEnded =
    !!meeting.endDatetime && moment().isAfter(moment(meeting.endDatetime));
  if (!meetingHasEnded && !hasGeneratedSummary) return null;

  const aiContainer = (
    <MeetingSidebarAIContainer
      meeting={meeting}
      transcriptMeeting={transcriptMeeting}
      transcriptLoading={loading}
      refetch={refetch}
      onDismiss={
        showSummaryWrapper || !dismissibleStateKey
          ? undefined
          : () =>
              dismiss(scopedDismissalKey(meetingGroupId, dismissibleStateKey))
      }
    >
      {({
        meeting: transcriptMeetingArg,
        refetch: refetchArg,
        loading: loadingArg,
      }) => (
        <div className={classNames("mt-4 text-sm flex flex-col gap-6")}>
          <MeetingSummaryGroupTranscription
            isCurrentMeeting={isCurrentMeeting}
            meeting={transcriptMeetingArg}
            meetingGroupId={meetingGroupId}
            refetch={refetchArg}
            loading={loadingArg}
            showVideoTranscriptModal={showVideoTranscriptModal}
            onCloseVideoTranscriptModal={handleCloseVideoTranscriptModal}
          />
        </div>
      )}
    </MeetingSidebarAIContainer>
  );

  if (!showSummaryWrapper) {
    return aiContainer;
  }

  const videoTranscriptButtonLabel =
    hasVideoData && hasTranscriptData
      ? "Video and Transcript"
      : hasVideoData
        ? "Video"
        : hasTranscriptData
          ? "Transcript"
          : null;

  const rightSide =
    summaryExpanded && videoTranscriptButtonLabel ? (
      <Button
        className="gap-2 shrink-0"
        size={ButtonSize.medium}
        theme={buttonTheme.text}
        onClick={() => setShowVideoTranscriptModal(true)}
      >
        <TopicflowIcon type="play" className="h-4 w-4" />
        {videoTranscriptButtonLabel}
      </Button>
    ) : null;

  return (
    <Layout.SidebarSection
      title="Summary"
      isExpanded={summaryExpanded}
      showExpansionControls
      onToggleExpand={setSummaryExpanded}
      className={classNames(
        "!px-0 !py-0 !border-0",
        summaryExpanded ? "!mb-12" : "!mb-6",
      )}
      titleClassName="tf-header-sm-dark"
      rightSide={rightSide}
    >
      {aiContainer}
    </Layout.SidebarSection>
  );
};

export default MeetingSummary;
